/* eslint-disable camelcase */
import createSDSIcon from "~/ui/icon";
import { CHECKER_NORMAL_24, CHEVRON_RIGHT_24, PLUS_OUTLINE_24 } from "@sznds/icons";
import { MAX_LENGTH, SUMMARY_PREFIX, REPORT_ADJUSTMENT, TEXTAREA_3_LINES, EMAIL_REGEXP, TEXTAREA_5_LINES } from "./const";
import { icon } from "~/util/dom";
import { getSuggestAddressData, getMapAddressData, getSuggestCategoryData, reportAnalytics, parseAttachments } from "./util";
import { IReportOpts, TSendOrigin, IReportData, TProject, IConfirmOptions, IAddressItem, TReportUpdateOptions, TNoteData, IMantisData, INewReportData, IReportFormData } from "./interfaces";
import { IPoi } from "~/interfaces";
import { getTitle, parsePhone } from "~/util/detail";
import { store } from "~/state/store";
import { _ } from "~/i18n";
import { CONFIG } from "~/config";
import { saveLocalChanges, saveLocalChangesNew } from "~/util/providers/report-local-changes";
import { isPointInStatePolygon } from "~/util/state-polygon";
import { getUserEmail } from "~/util/user";

function getEmptyNewSendData() {
	const curState = store.getState();
	const sendData: INewReportData = {
		platform: "web",
		additional_data: {
			email: "",
			mapset: curState.map.mapset,
			url: `${location.pathname}${location.search}`,
			zoom: curState.map.zoom,
			form_lang: CONFIG.defaultLang,
		},
		form_data: {},
	};

	return sendData;
}

function transformStateToFormData(values: any, state: any): IReportFormData {
	const copiedState = JSON.parse(JSON.stringify(state));
	const poiTypeId = state["category-data"] && typeof state["category-data"] !== "boolean" ? state["category-data"].poiTypeId : 0;

	// porovname se vstupem
	Object.keys(values).forEach(key => {
		if (key in copiedState) {
			const origValue = values[key];

			if (origValue === copiedState[key]) {
				delete copiedState[key];
			}
		}
	});

	const output: IReportFormData = {};

	if (copiedState.address && copiedState["address-data"]) {
		output.location = {
			lon: copiedState["address-data"].longitude,
			lat: copiedState["address-data"].latitude,
			address: copiedState.address,
			selected_from_map: copiedState["address-data"].locationObj?.selectedFromMap,
		};
	}

	const copyCategories = ["category", "email", "name", "opening", "phone", "www", "ico"];

	for (const category of copyCategories) {
		if (category in copiedState) {
			output[category] = copiedState[category];
		}
	}

	if (copiedState.note) {
		output.message = copiedState.note;
	}

	if (poiTypeId) {
		output.category_poitype_id = poiTypeId;
	}

	return output;
}

function getReportConfirmOptions(): IConfirmOptions {
	return {
		text: _("report.cancelFilledData"),
		confirmText: _("detail.content.review2.throwAway"),
		rejectText: _("back"),
	};
}

function hasChanged(state: any, originalValues: any): boolean {
	const wasChanged = Object.entries(state).some(entry => {
		const [key, value] = entry;

		return key !== "user-email" && originalValues[key] !== value;
	});
	const hasRemovedEntries = Object.keys(originalValues).filter(name => {
		const value = originalValues[name];

		// preskocime prazdne hodnoty
		if (["email", "phone", "www"].indexOf(name) !== -1 && value.length === 0) {
			return false;
		}

		return !(name in state);
	}).length > 0;

	return wasChanged || hasRemovedEntries;
}

export function validateEmail(value: string) {
	const isValid = value.match(EMAIL_REGEXP);

	return {
		status: isValid ? "valid" : "invalid",
		message: isValid ? "" : _("report.email.error"),
	};
}

function getReportPoiFields({
	values = {},
	detail,
	opening,
	inactive,
	temporarilyClosed,
}: {
	values?: any;
	detail?: any;
	opening?: any;
	inactive?: boolean;
	temporarilyClosed?: boolean;
}) {
	return [
		{
			type: "input",
			name: "category",
			label: _("fixedSuggest.category2.title"),
			sublabel: ` ${_("report.required")}`,
			placeholder: _("report.categoryPlaceholder"),
			value: values.category,
			suggestData: values["category-data"] || null,
			processSuggestData: getSuggestCategoryData,
			changeSign: detail ? icon("report-edit-location") : null,
			hasSuggest: true,
			notSelectedError: _("report.categoryClick"),
			notSuggestedError: _("report.categoryNotExist2"),
			setSuggestParams: paramsObj => {
				paramsObj.categoriesOnly = true;
				paramsObj.mapPoiTypeOnly = 1;
			},
			addFormFieldInputClass: !!detail,
			isHidden: "address-data" in values && values["address-data"]?.source === "firm",
		},
		{
			type: "input",
			name: "address",
			label: _("report.address"),
			sublabel: ` ${_("report.required")}`,
			placeholder: _("report.setMapPlace"),
			value: values.address,
			suggestData: values["address-data"] || null,
			processSuggestData: getSuggestAddressData,
			changeSign: detail ? icon("report-edit-location") : null,
			hasSuggest: true,
			notSelectedError: _("report.setMapClick"),
			notSuggestedError: _("report.addressNotExist"),
			setSuggestParams: paramsObj => {
				paramsObj.enableCategories = 0;
				paramsObj.type = "municipality|street|address";
			},
			updateStateHandler: (fieldObject, name, state) => {
				if (name === "map-address") {
					const { address } = state;
					const addressData: IAddressItem = state["address-data"];
					const input = fieldObject.getField("input-address");

					input.setValue(address, addressData);
				}
			},
			addFormFieldInputClass: !!detail,
		},
		{
			type: "map",
			name: "address",
			value: values["address-data"] || {},
			processMapData: getMapAddressData,
			updateStateHandler: (fieldObject, name, state) => {
				if (name === "input-address") {
					const data: IAddressItem = state["address-data"];
					const map = fieldObject.getField("map-address");

					if (data) {
						const { longitude, latitude, zoom } = data;

						map.changeMarkedPlace(longitude, latitude, zoom);
					} else {
						map.hideMarker();
					}
				}
			},
		},
		{
			type: "input",
			name: "name",
			label: _("report.placeName"),
			sublabel: "",
			placeholder: _("report.setPlaceName"),
			value: values.name || "",
			addFormFieldInputClass: !!detail,
			changeSign: detail ? icon("report-edit-location") : null,
			validate: () => ({
				status: "valid",
				message: "",
			}),
		},
		{
			type: "photos",
			name: "photos",
			getPhotoData: (photo: any, data: any) => {
				const { file, id } = photo;
				const date = new Date(file.lastModified);
				const { "address-data": address } = data;
				const { longitude = null, latitude = null } = address as IAddressItem;

				if (longitude === null || latitude === null) {
					return null;
				}

				return {
					id,
					entity: {
						id: `${longitude},${latitude}`,
						source: "coor",
					},
					name: "",
					takeDate: date.toISOString(),
					mark: {
						lon: longitude,
						lat: latitude,
					},
					madiaSource: "mapy",
				};
			},
		},
		{
			type: "detail",
			name: "detail",
			values: detail || null,
			label: _("report.describePlace"),
			opening,
			inactive,
			temporarilyClosed,
		},
		{
			type: "input",
			name: "user-email",
			label: _("report.email"),
			sublabel: ` ${_("report.required")}`,
			value: getUserEmail(),
			footnote: _("report.emailNote2"),
			validate: validateEmail,
			isEmail: true,
		},
	];
}

// uprava udaju
export function getSuggestDataOptions(detailData: IPoi, onNextStep: () => void): IReportOpts {
	// FIXME: nemela by sama sahat do store, mela by parametry dostavat
	const detail = {
		phone: "",
		email: "",
		www: "",
	};
	let inactive = false;
	let temporarilyClosed = false;

	({ inactive, temporarilyClosed } = detailData);
	const { titleVars, extend, mark, source, id } = detailData;
	const { contact, opening } = extend;
	const projectMapping = { firm: "katalog", pubt: "jr", addr: "linkcheck" };
	const project: TProject = source in projectMapping ? projectMapping[source] : "";

	if (contact) {
		const { email, phone, www } = contact;

		if (phone) {
			const phoneData = parsePhone(phone.value[0], " ");

			detail.phone = phoneData.number;
		}

		if (email) {
			detail.email = email.value[0];
		}

		if (www) {
			detail.www = www.value[0]?.text;
		}
	}

	const nameTitle = getTitle(detailData);
	const titleAsSpec = detailData.titleVars?.specification === nameTitle;
	const values = {
		"name": titleAsSpec
			? ""
			: nameTitle,
		"address": titleVars.locationMain1 || titleVars.titleMain,
		"address-data": {
			id,
			source,
			longitude: mark.lon,
			latitude: mark.lat,
			isKnownAddress: true,
			zoom: 0,
		},
		"category": titleVars.specification,
		"category-data": true,
		...detail,
	};

	return {
		type: "place-edit",
		name: REPORT_ADJUSTMENT,
		submitOptions: { project },
		popupContent: [
			{
				isExpanded: true,
				header: _("detail.content.closed.suggestEdit"),
				fields: [
					{
						type: "button",
						name: "edit",
						className: "form-field-edit-btn",
						text: _("report.changeAllEtc"),
						icon: createSDSIcon({ symbol: CHEVRON_RIGHT_24, className: "chevron-right" }),
						updateStateHandler: (fieldObject, name, state) => {
							if (name === "button-edit" && state.edit) {
								fieldObject.changeContent(1);
							}
						},
						onClick: () => onNextStep(),
					},
					{
						type: "button",
						name: "closed",
						className: "form-field-close-btn",
						text: _("report.placeNotExist"),
						icon: createSDSIcon({ symbol: CHECKER_NORMAL_24, className: "check-box" }),
						updateStateHandler: (fieldObject, name, state) => {
							const input = fieldObject.getField("input-user-email");
							const swtch = fieldObject.getField("button-edit");
							const textArea = fieldObject.getField("textarea-note");

							textArea?.container.classList.add("close-placed-textarea");

							if (name === "button-closed") {
								if (state.closed) {
									input.show();
									textArea?.show();
									swtch.disable();
								} else {
									input.hide();
									textArea?.hide();
									swtch.enable();
								}
							}

							fieldObject.getContainer().dispatchEvent(new Event("resize-content"));
						},
					},
					{
						type: "textarea",
						name: "note",
						label: _("report.note"),
						value: "",
						minHeight: TEXTAREA_3_LINES,
						placeholder: _("report.notePlaceholder"),
						isHidden: true,
					},
					{
						type: "input",
						name: "user-email",
						label: _("report.email"),
						sublabel: ` ${_("report.required")}`,
						validate: validateEmail,
						value: getUserEmail(),
						footnote: _("report.emailNote2"),
						isHidden: true,
						isEmail: true,
					},
				],
				submittability: state => (state["user-email"] || "").match(EMAIL_REGEXP) === null ? _("report.chooseOne") : null,
			},
			{
				header: _("report.editData"),
				fields: getReportPoiFields({
					values,
					detail,
					opening,
					inactive,
					temporarilyClosed,
				}),
				submittability: (state, originalValues) => {
					if (!state["address-data"]
						|| (state["user-email"] || "").match(EMAIL_REGEXP) === null
					) {
						return _("report.fillAllRequired");
					}

					if (state.email && state.email.match(EMAIL_REGEXP) === null) {
						return _("report.email.error");
					}

					const copied: any = {
						...originalValues,
					};

					if (!("category-data" in state)) {
						["category", "category-data"].forEach(name => {
							if (name in copied) {
								delete copied[name];
							}
						});
					}

					if (titleAsSpec && "name" in copied) {
						delete copied.name;
					}

					return hasChanged(state, copied) ? null : _("report.updateData");
				},
				confirmability: (state, originalValues) => hasChanged(state, originalValues),
				confirmOptions: {
					text: _("report.cancelChanges"),
					confirmText: _("detail.content.review2.throwAway"),
					rejectText: _("back"),
				},
			},
		],
		values,
		onSend: (sendData, metaData) => {
			const noteObj: { closed?: boolean } = sendData.noteData;

			saveLocalChanges(sendData, metaData);
			reportAnalytics({
				type: noteObj.closed ? "closedPlace" : "editPlace",
				origin: "poi",
				cz: sendData.isCzechia ? 1 : 0,
				...detailData
					? {
						source: detailData.source,
					}
					: {},
			}, "chyba-leti");
		},
		onSendNew: async (saveData, metaData) => {
			saveLocalChangesNew(saveData, metaData);

			const isCzechia = await isPointInStatePolygon(saveData.mark.lon, saveData.mark.lat, {
				cs: true,
			});

			reportAnalytics({
				type: metaData.opening ? "closedPlace" : "editPlace",
				origin: "poi",
				cz: isCzechia ? 1 : 0,
				...detailData
					? {
						source: detailData.source,
					}
					: {},
			}, "chyba-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.summary = `${SUMMARY_PREFIX}Úprava údajů`;
			mantisData.system = noteData.closed ? `mapycz_poi_closed` : `mapycz_poi_update`;
			mantisData.detailSource = detailData.source;
			mantisData.detailId = detailData.id as number;

			// porovname se vstupem
			Object.keys(values).forEach(key => {
				if (key in noteData) {
					const origValue = values[key];

					if (origValue === noteData[key]) {
						delete noteData[key];
					}
				}
			});

			if (noteData.address === "") {
				delete noteData.address;
			}

			// kvuli prokliku odkazu pridame mezeru na konec
			if (noteData.www) {
				noteData.www = `${noteData.www} `;
			}

			const link = [];

			if (noteData.mediaIds) {
				link.push(`https://admin.mapy.${CONFIG.TLD}/#/photoadmin/?id=${noteData.mediaIds.join(",")}`);
				delete noteData.mediaIds;
			}

			const noteStr = Object.keys(noteData).length > 0 ? JSON.stringify(noteData, null, "\t") : "Žádná změna";
			const attachmentData = parseAttachments(mantisData.attachments);

			const lines = [
				`Změna údajů:`,
				noteStr,
				...attachmentData ? [attachmentData] : [],
				...link.length > 0 ? [`Obrázky:`, link] : [],
				...noteData.location
					? [`Poloha na mapy.cz: https://mapy.cz/${mantisData.mapSet}?source=coor&id=${noteData.location.lon}%2C${noteData.location.lat}`]
					: [],
			];

			return {
				note: lines.join("\n"),
				noteData,
				mantisData,
			};
		},
		transformDataNew: (state, mediaIds) => {
			const reportData = getEmptyNewSendData();

			reportData.additional_data.email = state["user-email"];
			reportData.mark = {
				lon: mark.lon,
				lat: mark.lat,
			};
			reportData.form_data = transformStateToFormData(values, state);
			reportData.media_ids = mediaIds;

			if (state.closed) {
				reportData.form_data.closed = true;
			}

			return {
				reportData,
				source: detailData.source,
				id: detailData.id as number,
				opening: state.closed,
			};
		},
	};
}

export function getOpenAgainOptions(detailData: IPoi): IReportOpts {
	const { titleVars, mark, source, id } = detailData;
	const projectMapping = { firm: "katalog", pubt: "jr", addr: "linkcheck" };
	const project: TProject = source in projectMapping ? projectMapping[source] : "";
	const values = {
		"name": getTitle(detailData),
		"address": titleVars.locationMain1 || titleVars.titleMain,
		"address-data": {
			id,
			source,
			longitude: mark.lon,
			latitude: mark.lat,
			isKnownAddress: true,
			zoom: 0,
		},
		"category": titleVars.specification,
		"category-data": true,
	};

	return {
		type: "place-open-again",
		name: REPORT_ADJUSTMENT,
		submitOptions: { project },
		popupContent: [
			{
				isExpanded: false,
				header: _("report.suggestOpenAgain"),
				fields: [
					{
						type: "textarea",
						name: "note",
						label: _("report.noteOpenAgain"),
						value: "",
						minHeight: TEXTAREA_5_LINES,
						placeholder: _("report.notePlaceholder2"),
						isHidden: false,
					},
					{
						type: "photos",
						name: "photos",
						showInfoText: false,
						getPhotoData: (photo: any) => {
							const { file, id: photoId } = photo;
							const date = new Date(file.lastModified);
							const longitude = mark.lon;
							const latitude = mark.lat;

							if (longitude === null || latitude === null) {
								return null;
							}

							return {
								id: photoId,
								entity: {
									id: `${longitude},${latitude}`,
									source: "coor",
								},
								name: "",
								takeDate: date.toISOString(),
								mark: {
									lon: longitude,
									lat: latitude,
								},
								madiaSource: "mapy",
							};
						},
					},
					{
						type: "input",
						name: "user-email",
						label: _("report.email"),
						sublabel: ` ${_("report.required")}`,
						validate: validateEmail,
						value: getUserEmail(),
						footnote: _("report.emailNote2"),
						isHidden: false,
						isEmail: true,
					},
				],
				submittability: state => (state["user-email"] || "").match(EMAIL_REGEXP) === null ? _("reportDefect.form.emailError") : null,
			},
		],
		values,
		onSend: (sendData, metaData) => {
			saveLocalChanges(sendData, metaData);
			reportAnalytics({
				type: "closedPlace",
				origin: "poi",
				cz: sendData.isCzechia ? 1 : 0,
				...detailData
					? {
						source: detailData.source,
					}
					: {},
			}, "chyba-leti");
		},
		onSendNew: async (saveData, metaData) => {
			saveLocalChangesNew(saveData, metaData);

			const isCzechia = await isPointInStatePolygon(saveData.mark.lon, saveData.mark.lat, {
				cs: true,
			});

			reportAnalytics({
				type: "closedPlace",
				origin: "poi",
				cz: isCzechia ? 1 : 0,
				...detailData
					? {
						source: detailData.source,
					}
					: {},
			}, "chyba-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.summary = `${SUMMARY_PREFIX}Úprava údajů`;
			mantisData.system = `mapycz_poi_closed`;
			mantisData.detailSource = detailData.source;
			mantisData.detailId = detailData.id as number;

			// porovname se vstupem
			Object.keys(values).forEach(key => {
				if (key in noteData) {
					const origValue = values[key];

					if (origValue === noteData[key]) {
						delete noteData[key];
					}
				}
			});

			if (noteData.address === "") {
				delete noteData.address;
			}

			// kvuli prokliku odkazu pridame mezeru na konec
			if (noteData.www) {
				noteData.www = `${noteData.www} `;
			}

			const link = [];

			if (noteData.mediaIds) {
				link.push(`https://admin.mapy.${CONFIG.TLD}/#/photoadmin/?id=${noteData.mediaIds.join(",")}`);
				delete noteData.mediaIds;
			}

			noteData.closed = false;

			const noteStr = Object.keys(noteData).length > 0 ? JSON.stringify(noteData, null, "\t") : "Žádná změna";
			const attachmentData = parseAttachments(mantisData.attachments);

			const lines = [
				`Změna údajů:`,
				noteStr,
				...attachmentData ? [attachmentData] : [],
				...link.length > 0 ? [`Obrázky:`, link] : [],
				...noteData.location
					? [`Poloha na mapy.cz: https://mapy.cz/${mantisData.mapSet}?source=coor&id=${noteData.location.lon}%2C${noteData.location.lat}`]
					: [],
			];

			return {
				note: lines.join("\n"),
				noteData,
				mantisData,
			};
		},
		transformDataNew: (state, mediaIds) => {
			const reportData = getEmptyNewSendData();

			reportData.additional_data.email = state["user-email"];
			reportData.mark = {
				lon: mark.lon,
				lat: mark.lat,
			};
			reportData.form_data = transformStateToFormData(values, state);
			reportData.form_data.closed = false;
			reportData.media_ids = mediaIds;

			return {
				reportData,
				source: detailData.source,
				id: detailData.id as number,
				opening: true,
			};
		},
	};
}

export function getMapErrorOptions(origin: TSendOrigin = "settings"): IReportOpts {
	return {
		type: "map-error",
		name: "report-error",
		submitOptions: { project: "" },
		createDraw: true,
		dialogConfirmation: true,
		popupContent: [
			{
				header: _("report.mapError"),
				fields: [
					{
						type: "textarea",
						name: "defect",
						label: _("report.userMapError"),
						sublabel: _("report.makeANote"),
						subsublabel: [_("report.drawInfo"), _("report.reportError.info")],
						placeholder: _("detail.content.feedback.noteLabel"),
						minHeight: 138,
						isExtended: true,
					},
					{
						type: "loader",
						name: "attachment",
						label: _("report.addFile"),
						sublabel: _("report.filesExt"),
						icon: createSDSIcon({ symbol: PLUS_OUTLINE_24, className: "plus-outline" }),
						text: _("report.chooceFileOrDrag"),
						isExtended: true,
					},
					{
						type: "input",
						name: "user-email",
						label: _("report.email"),
						sublabel: ` ${_("report.required")}`,
						validate: validateEmail,
						value: getUserEmail(),
						footnote: _("report.emailNote2"),
						isEmail: true,
					},
				],
				isMaximal: true,
				submittability: state => (state.defect || (Array.isArray(state.drawUtil) && state.drawUtil.length > 0)) && (state["user-email"] || "").match(EMAIL_REGEXP) !== null ? null : _("report.mapErrorProblem"),
				confirmability: state => state.defect || state.attachment || (Array.isArray(state.drawUtil) && state.drawUtil.length > 0),
				confirmOptions: {
					confirmText: _("report.mapError.dialog.title"),
					rejectText: _("report.mapError.dialog.title"),
					text: _("report.mapError.dialog.desc"),
				},
			},
		],
		onSend: () => {
			reportAnalytics({
				type: "mistake",
				origin,
			}, "chyba-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.system = `mapycz_map_error`;

			const attachmentData = parseAttachments(mantisData.attachments);
			const note = [
				...noteData.defect ? [noteData.defect] : ["Bez popisu"],
				...attachmentData ? [attachmentData] : [],
			];

			return {
				note: note.join(", "),
				noteData,
				mantisData,
			};
		},
	};
}

export function getMapErrorOnUpdate(name: TReportUpdateOptions) {
	const resizerCont = document.querySelector(".map-controls__resizer");

	if (name === "start-anim-begin") {
		resizerCont && resizerCont.classList.add("report-map-anim");
		resizerCont && resizerCont.classList.add("anim-width");
	} else if (name === "close") {
		resizerCont && resizerCont.classList.remove("anim-width");

		// musime pote odstranit tridu
		setTimeout(() => {
			resizerCont && resizerCont.classList.remove("report-map-anim");
		}, 1000);
	}
}

export function getPremiumProblem(msg?: string, origin: TSendOrigin = "settings"): IReportOpts {
	return {
		name: "report-defect",
		type: "defect",
		submitOptions: { project: "premium" },
		staticHeight: true,
		popupContent: [
			{
				header: _("report.premiumTitle"),
				fields: [
					{
						type: "textarea",
						name: "defect",
						label: _("report.premiumTextareaLabel"),
						placeholder: _("report.premiumTextareaPlaceholder"),
						isExtended: true,
						minHeight: 138,
						...msg
							? {
								insertHTML: true,
								value: msg,
							}
							: {},
					},
					{
						type: "loader",
						name: "attachment",
						label: _("report.addFile"),
						sublabel: _("report.premiumFileType"),
						icon: createSDSIcon({ symbol: PLUS_OUTLINE_24, className: "plus-outline" }),
						text: _("report.chooceFileOrDrag"),
						isExtended: true,
					},
					{
						type: "input",
						name: "user-email",
						label: _("report.email"),
						sublabel: ` ${_("report.required")}`,
						validate: validateEmail,
						value: getUserEmail(),
						footnote: _("report.emailNote2"),
						isEmail: true,
					},
				],
				submittability: state => state.defect && (state["user-email"] || "").match(EMAIL_REGEXP) !== null ? null : _("report.describeProblemPremium"),
				confirmability: state => state.defect || state.attachment,
				confirmOptions: getReportConfirmOptions(),
			},
		],
		onSend: () => {
			reportAnalytics({
				type: "premium",
				origin,
			}, "chyba-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.summary = `${SUMMARY_PREFIX}Problémy s prémiem`;
			mantisData.system = `mapycz_error`;

			const attachmentData = parseAttachments(mantisData.attachments);
			const note = [
				...noteData.defect ? [noteData.defect] : [],
				...attachmentData ? [attachmentData] : [],
			];

			return {
				note: note.join(", "),
				noteData,
				mantisData,
			};
		},
	};
}

export function getReportDefectOptions(msg: string, origin: TSendOrigin = "settings"): IReportOpts {
	return {
		type: "defect",
		name: "report-defect",
		submitOptions: { project: "product" },
		staticHeight: true,
		popupContent: [
			{
				header: _("report.somethingWentWrong"),
				fields: [
					{
						type: "textarea",
						name: "defect",
						label: _("report.userThingWentWrong"),
						sublabel: _("report.makeANote"),
						placeholder: _("reportDefect.form.msgPlaceholder"),
						isExtended: true,
						minHeight: 138,
						...msg
							? {
								insertHTML: true,
								value: msg,
							}
							: {},
					},
					{
						type: "loader",
						name: "attachment",
						label: _("report.addFile"),
						sublabel: _("report.filesExt"),
						icon: createSDSIcon({ symbol: PLUS_OUTLINE_24, className: "plus-outline" }),
						text: _("report.chooceFileOrDrag"),
						isExtended: true,
					},
					{
						type: "input",
						name: "user-email",
						label: _("report.email"),
						sublabel: ` ${_("report.required")}`,
						validate: validateEmail,
						value: getUserEmail(),
						footnote: _("report.emailNote2"),
						isEmail: true,
					},
				],
				submittability: state => state.defect && (state["user-email"] || "").match(EMAIL_REGEXP) !== null ? null : _("report.describeProblem"),
				confirmability: state => state.defect || state.attachment,
				confirmOptions: getReportConfirmOptions(),
			},
		],
		onSend: () => {
			reportAnalytics({
				type: "broken",
				origin,
			}, "chyba-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.summary = origin === "routing" ? `${SUMMARY_PREFIX}Chyba v plánování` : `${SUMMARY_PREFIX}Něco nefunguje`;
			mantisData.system = origin === "routing" ? `route_planner_error` : `mapycz_error`;

			const attachmentData = parseAttachments(mantisData.attachments);
			const note = [
				...noteData.defect ? [noteData.defect] : [],
				...attachmentData ? [attachmentData] : [],
			];

			return {
				note: note.join(", "),
				noteData,
				mantisData,
			};
		},
	};
}

export function getMapNoteOptions(): IReportOpts {
	return {
		type: "map-note",
		name: "report-observation",
		submitOptions: { project: "product" },
		staticHeight: true,
		popupContent: [
			{
				header: _("report.mapyczNote"),
				fields: [
					{
						type: "textarea",
						name: "observation",
						label: _("report.suggestionOrNote"),
						sublabel: _("report.makeANote"),
						placeholder: _("report.textPlaceholder"),
						isExtended: true,
						minHeight: 138,
					},
					{
						type: "input",
						name: "user-email",
						label: _("report.email"),
						sublabel: ` ${_("report.required")}`,
						validate: validateEmail,
						value: getUserEmail(),
						footnote: _("report.emailNote2"),
						isEmail: true,
					},
				],
				submittability: state => state.observation && (state["user-email"] || "").match(EMAIL_REGEXP) !== null ? null : _("report.startTyping"),
				confirmability: state => state.observation,
				confirmOptions: getReportConfirmOptions(),
			},
		],
		onSend: () => {
			reportAnalytics({
				type: "notice",
				origin: "settings",
			}, "chyba-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.summary = `${SUMMARY_PREFIX}Připomínka k mapy.cz`;
			mantisData.system = `mapycz_note`;

			return {
				note: noteData.observation || "",
				noteData,
				mantisData,
			};
		},
	};
}

export function getReportPanoramaOptions(pid: number, holderParent: HTMLElement): IReportOpts {
	return {
		type: "pano",
		name: "report-panorama",
		submitOptions: { project: "panorama" },
		holderParent,
		popupContent: [
			{
				header: _("report.panorama"),
				fields: [
					{
						type: "switch",
						name: "problem",
						items: [
							{
								value: "face",
								label: _("report.panoramaError.face"),
							},
							{
								value: "spz",
								label: _("report.panoramaError.spz"),
							},
							{
								value: "other",
								label: _("report.panoramaError.other"),
							},
						],
					},
					{
						type: "textarea",
						name: "description",
						label: _("report.note"),
						placeholder: _("detail.content.feedback.noteLabel"),
						minHeight: 104,
						maxlength: MAX_LENGTH,
						getMessage: value => _("detail.content.review.numOfChar", { chars: MAX_LENGTH - value.length }),
					},
					{
						type: "input",
						name: "user-email",
						label: "",
						sublabel: _("reportDefect.form.emailPlaceholder"),
						value: getUserEmail(),
						footnote: _("report.emailNote2"),
						isEmail: true,
					},
				],
				submittability: state => (state.problem !== "other" || state.description) && (state["user-email"] || "").match(EMAIL_REGEXP) !== null ? null : _("report.describeProblemInTheNote"),
				confirmability: state => state.observation || state.description,
				confirmOptions: getReportConfirmOptions(),
			},
		],
		onSend: sendData => {
			const noteObj = sendData.noteData as { description?: string; problem: "face" | "spz" | "other" };

			reportAnalytics({
				text: noteObj.description ? 1 : 0,
				section: "panorama",
				type: noteObj.problem === "face" ? 1 : noteObj.problem === "spz" ? 2 : 3,
			}, "problem-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.summary = `${SUMMARY_PREFIX}Připomínka k mapy.cz`;
			mantisData.system = `mapycz_panorama`;

			let problem = _("report.panoramaError.face");

			if (noteData.problem === "spz") {
				problem = _("report.panoramaError.spz");
			} else if (noteData.problem === "other") {
				problem = _("report.panoramaError.other");
			}

			const note = [
				`Problém panoramy ${pid}: ${problem}`,
				...noteData.description ? [`popis problému: ${noteData.description}`] : [],
			];

			return {
				note: note.join(", "),
				noteData,
				mantisData,
			};
		},
	};
}

interface IGetAddPlaceOptions {
	values: any;
	analytics: Partial<IReportData>;
	project?: TProject;
	coords?: INewReportData["mark"];
}

export function getAddPlaceOptions({
	values,
	analytics,
	project = "",
	coords,
}: IGetAddPlaceOptions): IReportOpts {
	return {
		type: "place-add",
		name: "report-poi",
		submitOptions: { project },
		popupContent: [
			{
				header: _("report.addPlace2"),
				fields: getReportPoiFields({
					values: {
						...values,
						name: "",
					},
				}),
				submittability: (state: any): string|null => {
					if (!state["address-data"]
						|| !state["category-data"]
						|| (state["user-email"] || "").match(EMAIL_REGEXP) === null
					) {
						return _("report.fillAllRequired");
					}

					return null;
				},
				confirmability: (state: any): boolean => {
					const origAdr = values["address-data"];
					const adr = state["address-data"];
					const addressChange = (!origAdr && adr) || (adr && (origAdr && (origAdr.longitude !== adr.longitude || origAdr.latitude !== adr.latitude)));

					return (
						state["category-data"]
						|| addressChange
						|| state.name
						// eslint-disable-next-line prefer-spread
						|| Array.apply(null, { length: 7 }).some((value, index) => state[`opening-${index + 1}`])
						|| state.web
						|| state.phone
						|| state.email
						|| state.bank
						|| state.note
						|| state.photos
						|| state.attachment
					);
				},
				confirmOptions: getReportConfirmOptions(),
			},
		],
		onSend: sendData => {
			reportAnalytics({
				...analytics,
				cz: sendData.isCzechia ? 1 : 0,
			}, "chyba-leti");
		},
		onSendNew: async saveData => {
			const isCzechia = await isPointInStatePolygon(saveData.mark.lon, saveData.mark.lat, {
				cs: true,
			});

			reportAnalytics({
				...analytics,
				cz: isCzechia ? 1 : 0,
			}, "chyba-leti");
		},
		transformData: (noteData: TNoteData, mantisData: IMantisData) => {
			mantisData.summary = `${SUMMARY_PREFIX}Nový bod`;
			mantisData.system = `mapycz_new_place`;

			const link = [];

			if (noteData.mediaIds) {
				link.push(`https://admin.mapy.${CONFIG.TLD}/#/photoadmin/?id=${noteData.mediaIds.join(",")}`);
				delete noteData.mediaIds;
			}

			// kvuli prokliku odkazu pridame mezeru na konec
			if (noteData.www) {
				noteData.www = `${noteData.www} `;
			}

			return {
				note: [
					`Nové místo:`,
					JSON.stringify(noteData, null, "\t"),
					...link.length > 0 ? [`Obrázky:`, link] : [],
					...noteData.location
						? [`Poloha na mapy.cz: https://mapy.cz/${mantisData.mapSet}?source=coor&id=${noteData.location.lon}%2C${noteData.location.lat}`]
						: [],
				].join("\n"),
				noteData,
				mantisData,
			};
		},
		transformDataNew: (state, mediaIds) => {
			const reportData = getEmptyNewSendData();

			reportData.additional_data.email = state["user-email"];
			reportData.mark = coords;
			reportData.form_data = transformStateToFormData({}, state);
			reportData.media_ids = mediaIds;

			return {
				reportData,
			};
		},
	};
}
